import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

export interface AutocompleteOption {
  id: string;
  name: string;
  value: string;
  color?: string;
  badge?: string;
  description?: string;
}

interface AutocompleteComboboxProps {
  options: AutocompleteOption[];
  value?: string;
  values?: string[]; // For multiselect mode
  onValueChange?: (value: string) => void; // For single select
  onValuesChange?: (values: string[]) => void; // For multiselect
  placeholder?: string;
  emptyMessage?: string;
  allowCreate?: boolean;
  createLabel?: (value: string) => string;
  className?: string;
  disabled?: boolean;
  multiselect?: boolean;
  maxSelections?: number;
}

export function AutocompleteCombobox({
  options,
  value,
  values = [],
  onValueChange,
  onValuesChange,
  placeholder = "Search or select...",
  emptyMessage = "No items found",
  allowCreate = false,
  createLabel = (value) => `Create "${value}"`,
  className,
  disabled = false,
  multiselect = false,
  maxSelections,
}: AutocompleteComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  const selectedOption = options.find(option => option.value === value);
  const selectedOptions = multiselect ? options.filter(option => values.includes(option.value)) : [];

  const handleValueSelect = (optionValue: string) => {
    if (multiselect && onValuesChange) {
      const newValues = values.includes(optionValue)
        ? values.filter(v => v !== optionValue)
        : maxSelections && values.length >= maxSelections
          ? values
          : [...values, optionValue];
      onValuesChange(newValues);
    } else if (!multiselect && onValueChange) {
      onValueChange(optionValue);
      setOpen(false);
    }
  };

  const handleCreateNew = (newValue: string) => {
    if (multiselect && onValuesChange) {
      if (maxSelections && values.length >= maxSelections) return;
      onValuesChange([...values, newValue]);
    } else if (!multiselect && onValueChange) {
      onValueChange(newValue);
      setOpen(false);
    }
  };

  const removeSelection = (valueToRemove: string) => {
    if (multiselect && onValuesChange) {
      onValuesChange(values.filter(v => v !== valueToRemove));
    }
  };

  return (
    <div className="space-y-2">
      {/* Show selected items for multiselect mode */}
      {multiselect && selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((option) => (
            <Badge key={option.id} variant="secondary" className="flex items-center gap-1">
              {option.color && (
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: option.color }}
                />
              )}
              {option.name}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  removeSelection(option.value);
                }}
                className="ml-1 hover:bg-secondary-foreground/10 rounded-sm"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <Popover
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen);
          if (!newOpen) {
            setInputValue("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between", className)}
            disabled={disabled}
          >
            {multiselect ? (
              selectedOptions.length > 0
                ? maxSelections
                  ? t("autocompleteCombobox.selectedCountMax", { count: selectedOptions.length, max: maxSelections })
                  : t("autocompleteCombobox.selectedCount", { count: selectedOptions.length })
                : placeholder
            ) : (
              selectedOption?.name || value || placeholder
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={inputValue}
            onValueChange={(value) => {
              setInputValue(value);
            }}
          />
          <CommandList>
            <CommandEmpty>
              {inputValue && allowCreate && (!maxSelections || values.length < maxSelections) ? (
                <div className="p-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    {emptyMessage}
                  </p>
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      handleCreateNew(inputValue);
                      setInputValue("");
                      if (!multiselect) setOpen(false);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {createLabel(inputValue)}
                  </Button>
                </div>
              ) : (
                <div className="p-2">
                  <p className="text-sm text-muted-foreground">
                    {maxSelections && values.length >= maxSelections
                      ? t("autocompleteCombobox.maxReached", { max: maxSelections })
                      : emptyMessage
                    }
                  </p>
                </div>
              )}
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => {
                const isSelected = multiselect
                  ? values.includes(option.value)
                  : value === option.value;
                const isDisabled = multiselect && maxSelections && values.length >= maxSelections && !isSelected;

                return (
                  <CommandItem
                    key={option.id}
                    value={option.name}
                    disabled={isDisabled}
                    onSelect={() => {
                      if (!isDisabled) {
                        handleValueSelect(option.value);
                        setInputValue("");
                      }
                    }}
                  >
                    <div className="flex items-center gap-2 w-full">
                      {option.color && (
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: option.color }}
                        />
                      )}
                      <span className={isDisabled ? "opacity-50" : ""}>{option.name}</span>
                      {option.badge && (
                        <Badge variant="outline" className="text-xs ml-auto">
                          {option.badge}
                        </Badge>
                      )}
                    </div>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    </div>
  );
}